import AutomationsMap from '@/components/AutomationsMap';
import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionHelp from '@/components/help/SectionHelp';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, useReactFlow, Handle, Position, reconnectEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Workflow, Plus, Trash2, ArrowLeft, Save, Play, MessageCircle, ClipboardList, PhoneCall,
  FileText, Bot, Mail, Shield, Users, Clock, Megaphone, GitBranch, Loader2, X, UserPlus,
  Send, CheckSquare, StickyNote, BellRing, CalendarPlus, Star, Hand, Split, Sparkles, Globe, Share2, MessageSquare, PhoneForwarded,
  Download, Upload,
} from 'lucide-react';

const ICONS = {
  MessageCircle, ClipboardList, PhoneCall, FileText, Bot, Mail, Shield, Users, Clock, Megaphone, GitBranch, Workflow, UserPlus,
  Send, CheckSquare, StickyNote, BellRing, CalendarPlus, Star, Hand, Split, Globe,
  // Los pedía el catálogo y no estaban: tres nodos salían con el engrane
  // genérico e indistinguibles entre sí.
  MessageSquare, PhoneForwarded, Sparkles,
};

// Un color por categoría. Faltaban `crm`, `agenda` y `marketing`, así que sus
// nodos caían al respaldo —el ámbar de «Control y tiempo»— y en el lienzo un
// «Automatización de marketing» se veía idéntico a un «Esperar tiempo».
const CAT_COLORS = {
  entry: { border: '#10B981', bg: '#ECFDF5', text: '#047857' },
  messaging: { border: '#3B82F6', bg: '#EFF6FF', text: '#1D4ED8' },
  assignment: { border: '#8B5CF6', bg: '#F5F3FF', text: '#6D28D9' },
  crm: { border: '#0EA5E9', bg: '#F0F9FF', text: '#0369A1' },
  agenda: { border: '#14B8A6', bg: '#F0FDFA', text: '#0F766E' },
  marketing: { border: '#EC4899', bg: '#FDF2F8', text: '#BE185D' },
  control: { border: '#F59E0B', bg: '#FFFBEB', text: '#B45309' },
};


/** S181 — texto humano bajo la tarjeta (evita confusiones tipo "+9" = 9 am). */
function buildNodeSummary(nodeType, config = {}) {
  const cfg = config || {};
  if (nodeType === 'book_appointment' || nodeType === 'book_service') {
    const mode = cfg.schedule_mode || 'next_business_slot';
    const dur = cfg.duration_minutes ? ` · ${cfg.duration_minutes} min` : '';
    if (mode === 'next_business_slot' || !mode) {
      return `Próximo hueco libre del horario del negocio${dur}`;
    }
    if (mode === 'at_clock_time') {
      const t = (cfg.clock_time || '09:00').trim() || '09:00';
      return `A las ${t} (hora del reloj, dentro del horario)${dur}`;
    }
    const h = Number(cfg.starts_in_hours);
    if (!Number.isFinite(h) || h <= 0) return 'Modo avanzado: indica horas desde ahora';
    return `Dentro de ${h} h desde este momento (no es hora del reloj)${dur}`;
  }
  if (nodeType === 'ai_agent') {
    const mode = cfg.tools_mode || 'personalizado';
    if (mode === 'ninguna') return 'Solo conversa (sin herramientas)';
    if (mode === 'canal') return 'Herramientas = Config IA del canal';
    const n = Array.isArray(cfg.tools) ? cfg.tools.length : 0;
    return n ? `${n} herramienta(s) elegidas para este paso` : 'Elige herramientas abajo (recomendado)';
  }
  if (nodeType === 'marketing_automation') {
    return cfg.flow_id ? 'Secuencia de Marketing enlazada' : 'Elige secuencia (Marketing → Automáticos)';
  }
  if (nodeType === 'wait_time') {
    const v = Number(cfg.value);
    const unit = cfg.unit || 'hours';
    if (!Number.isFinite(v)) return 'Configura el tiempo de espera';
    const u = unit === 'minutes' ? (v === 1 ? 'minuto' : 'minutos')
      : unit === 'hours' ? (v === 1 ? 'hora' : 'horas')
      : unit === 'days' ? (v === 1 ? 'día' : 'días') : unit;
    return `Espera ${v} ${u} y sigue`;
  }
  if (nodeType === 'connect_lead_to_vendor') {
    const mode = (cfg.mode || 'ivr') === 'bridge' ? 'puente (los dos a la vez)' : 'IVR (menú al lead)';
    const who = cfg.vendor_resolution === 'on_duty_guard' ? 'guardia' : 'vendedor asignado';
    return `${mode} · ${who}`;
  }
  if (nodeType === 'update_pipeline_stage') {
    return cfg.stage ? `Mover a etapa: ${cfg.stage}` : 'Elige la etapa';
  }
  return cfg.summary || '';
}

function previewHoursFromNow(hours) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return null;
  const d = new Date(Date.now() + h * 3600 * 1000);
  try {
    return d.toLocaleString('es-MX', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return d.toISOString();
  }
}

// ── Custom node: icon + label + handles en 4 lados (mejor acomodo visual) ──
// S145c: target/source en top/bottom/left/right. Condición mantiene Sí/No
// en la base (true/false) y además acepta entrada por lados.
function FlowNode({ data, selected }) {
  const Icon = ICONS[data.icon] || Workflow;
  const c = CAT_COLORS[data.category] || CAT_COLORS.control;
  const isCondition = data.nodeType === 'condition';
  const hStyle = { background: c.border, width: 10, height: 10 };
  return (
    <div style={{ borderColor: selected ? c.border : '#E2E8F0', background: c.bg }}
      className={`relative rounded-xl border-2 px-3 py-2 shadow-sm ${isCondition ? 'min-w-[200px] pb-5' : 'min-w-[170px]'}`}>
      {/* Entradas: desde cualquier lado se puede conectar hacia este nodo */}
      <Handle type="target" id="t-top" position={Position.Top} style={hStyle} />
      <Handle type="target" id="t-left" position={Position.Left} style={{ ...hStyle, top: '50%' }} />
      <Handle type="target" id="t-right" position={Position.Right} style={{ ...hStyle, top: '35%' }} />
      <Handle type="target" id="t-bottom" position={Position.Bottom} style={{ ...hStyle, left: isCondition ? '10%' : '50%' }} />

      <div className="flex items-center gap-2">
        <span className="rounded-lg p-1.5" style={{ background: c.border }}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </span>
        <span className="text-sm font-semibold" style={{ color: c.text }}>{data.label}</span>
      </div>
      {(() => {
        const s = data.summary || buildNodeSummary(data.nodeType, data.config);
        return s ? <p className="text-[11px] text-slate-500 mt-1 pl-1 max-w-[200px] leading-snug">{s}</p> : null;
      })()}

      {isCondition ? (
        <>
          <span className="absolute text-[10px] font-semibold text-emerald-600" style={{ bottom: 2, left: '28%' }}>Sí</span>
          <span className="absolute text-[10px] font-semibold text-red-600" style={{ bottom: 2, left: '68%' }}>No</span>
          <Handle type="source" id="true" position={Position.Bottom} style={{ left: '35%', background: '#10B981', width: 10, height: 10 }} />
          <Handle type="source" id="false" position={Position.Bottom} style={{ left: '75%', background: '#EF4444', width: 10, height: 10 }} />
          <Handle type="source" id="s-left" position={Position.Left} style={{ ...hStyle, top: '70%' }} />
          <Handle type="source" id="s-right" position={Position.Right} style={{ ...hStyle, top: '70%' }} />
        </>
      ) : (
        <>
          <Handle type="source" id="s-bottom" position={Position.Bottom} style={hStyle} />
          <Handle type="source" id="s-left" position={Position.Left} style={{ ...hStyle, top: '70%' }} />
          <Handle type="source" id="s-right" position={Position.Right} style={{ ...hStyle, top: '70%' }} />
          <Handle type="source" id="s-top" position={Position.Top} style={{ ...hStyle, left: '70%' }} />
        </>
      )}
    </div>
  );
}

const NODE_TYPES = { cavernico: FlowNode };
let nodeSeq = 1;

/** S147: al guardar, solo id/type/position/data — sin width/selected/dragging
 *  de React Flow, que al recargar reordenan el lienzo. */
function sanitizeGraph(nodes, edges) {
  return {
    nodes: (nodes || []).map(n => ({
      id: n.id,
      type: n.type || 'cavernico',
      position: {
        x: Number(n.position?.x) || 0,
        y: Number(n.position?.y) || 0,
      },
      data: {
        nodeType: n.data?.nodeType,
        label: n.data?.label,
        icon: n.data?.icon,
        category: n.data?.category,
        config: n.data?.config || {},
        ...(n.data?.summary ? { summary: n.data.summary } : {}),
      },
    })),
    edges: (edges || []).map((e, i) => ({
      id: e.id || `e-${e.source}-${e.target}-${i}`,
      source: e.source,
      target: e.target,
      ...(e.sourceHandle ? { sourceHandle: e.sourceHandle } : {}),
      ...(e.targetHandle ? { targetHandle: e.targetHandle } : {}),
      animated: e.animated !== false,
    })),
  };
}

// ── Config panel for the selected node ────────────────────────────────
// Exportado SOLO para el guardia de render, igual que FlowEditor: el panel
// aparece al seleccionar un nodo —estado interno del lienzo— y sin poder
// montarlo suelto no había forma de comprobar que sus campos se pintan.
export function NodeConfigPanel({ node, catalog, onChange, onLabelChange, onClose, onDelete }) {
  const def = useMemo(() => catalog?.nodes?.find(n => n.key === node?.data?.nodeType), [catalog, node]);
  const [refOptions, setRefOptions] = useState({});
  // Sincronía con «Mi Negocio → Asignación»: el nodo connect_lead_to_vendor NO
  // guarda su on/off en el flujo — lo lee del interruptor de Asignación
  // (auto_ivr_on_first_link), una sola verdad. Aquí se muestra y se puede cambiar.
  const [ivrCfg, setIvrCfg] = useState(null);
  const esNodoIvr = node?.data?.nodeType === 'connect_lead_to_vendor';
  useEffect(() => {
    if (!esNodoIvr) return;
    api.get('/tenant/assignment/config').then(r => setIvrCfg(r.data)).catch(() => setIvrCfg(null));
  }, [esNodoIvr]);
  const alternarIvr = async () => {
    if (!ivrCfg) return;
    const nuevo = { ...ivrCfg, auto_ivr_on_first_link: !ivrCfg.auto_ivr_on_first_link };
    setIvrCfg(nuevo);
    try {
      await api.put('/tenant/assignment/config', nuevo);
    } catch (e) {
      setIvrCfg(ivrCfg); // revertir si falla
    }
  };

  useEffect(() => {
    if (!def) return;
    // `multi` también trae su lista de un `ref`: sin incluirlo aquí, las
    // casillas de funciones se pintaban vacías siempre.
    def.config.filter(f => (f.type === 'ref' || f.type === 'multi') && f.ref).forEach(f => {
      api.get(`/tenant/flows/refs/${f.ref}`).then(r => setRefOptions(o => ({ ...o, [f.ref]: r.data }))).catch(() => {});
    });
    // Las variables se cargan siempre que el nodo tenga algún texto libre: sin
    // verlas listadas, «¿puedo poner el nombre del cliente?» solo se responde
    // adivinando.
    if (def.config.some(f => f.type === 'textarea' || f.type === 'text')) {
      api.get('/tenant/flows/refs/variable')
        .then(r => setRefOptions(o => ({ ...o, variable: r.data }))).catch(() => {});
    }
  }, [def]);

  if (!node || !def) return null;
  const cfg = node.data.config || {};
  const setField = (key, val) => onChange({ ...cfg, [key]: val });

  // Un campo puede depender de otro: las «Funciones permitidas» solo tienen
  // sentido si antes elegiste «Elegir yo». Mostrarlas siempre sería ofrecerte
  // marcar casillas que el servidor va a ignorar.
  const aplica = (f) => {
    const d = f.depends_on;
    if (!d) return true;
    return (cfg[d.key] ?? def.config.find(c => c.key === d.key)?.default) === d.value;
  };
  const valorMulti = (key) => (Array.isArray(cfg[key]) ? cfg[key] : []);
  const alternarMulti = (key, opcion) => {
    const actual = valorMulti(key);
    setField(key, actual.includes(opcion)
      ? actual.filter(v => v !== opcion)
      : [...actual, opcion]);
  };

  return (
    <div className="w-72 border-l border-[#E2E8F0] bg-white p-4 overflow-y-auto" data-testid="flow-node-config">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-[#0F172A]">{def.label}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-xs text-[#64748B] mb-3">{def.help}</p>
      {def.key === 'ai_agent' && (
        <div className="mb-3 rounded-lg border border-violet-100 bg-violet-50 px-2.5 py-2 text-[11px] text-violet-950 leading-snug" data-testid="flow-ai-tools-callout">
          <p className="font-semibold mb-0.5">Estándar de excelencia — herramientas</p>
          <p>
            1) Elige <strong>«Elegir funciones de excelencia»</strong>.
            2) Marca las funciones (agendar, pedir datos, catálogo, enlazar vendedor…).
            3) El inventario maestro está en <strong>Config IA → Herramientas</strong>; aquí fijas las de este paso del flujo.
          </p>
        </div>
      )}
      {def.key === 'marketing_automation' && (
        <div className="mb-3 rounded-lg border border-amber-100 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-950 leading-snug" data-testid="flow-mkt-auto-callout">
          <p className="font-semibold mb-0.5">Secuencias email / WhatsApp</p>
          <p>
            Se crean y editan en <strong>Marketing → Automáticos</strong>
            (cumpleaños, bienvenida, recordatorio de cita, reactivación…).
            Aquí solo eliges cuál disparar en este punto del flujo.
          </p>
        </div>
      )}
      {def.key === 'connect_lead_to_vendor' && (
        <div className="mb-3 rounded-lg border border-sky-100 bg-sky-50 px-2.5 py-2 text-[11px] text-sky-950 leading-snug" data-testid="flow-ivr-callout">
          <p className="font-semibold mb-0.5">Llamada automática (IVR) al asignar</p>
          <p className="mb-2">
            Este nodo llama al lead, le pide «presiona 1» y lo conecta con su
            vendedor. Se enciende/apaga en <strong>Mi Negocio → Asignación</strong>
            y aquí es el <strong>mismo interruptor</strong> (una sola verdad).
            Apagado por defecto.
          </p>
          <button
            type="button"
            onClick={alternarIvr}
            disabled={ivrCfg === null}
            data-testid="flow-ivr-toggle"
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold border transition-colors ${
              ivrCfg?.auto_ivr_on_first_link
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-slate-600 border-slate-300'}`}>
            <span className={`w-2 h-2 rounded-full ${ivrCfg?.auto_ivr_on_first_link ? 'bg-white' : 'bg-slate-400'}`} />
            {ivrCfg === null ? 'Cargando…' : (ivrCfg.auto_ivr_on_first_link ? 'Activada (toca para apagar)' : 'Apagada (toca para encender)')}
          </button>
        </div>
      )}
      {def.key === 'book_appointment' && (
        <div className="mb-3 rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-2 text-[11px] text-emerald-950 leading-snug">
          <p className="font-semibold mb-0.5">Horario del negocio</p>
          <p>
            Usa <strong>próximo hueco libre</strong> o <strong>hora del reloj</strong> (ej. 09:00 = 9 am).
            Evita «dentro de X horas» salvo casos técnicos: no es la hora del reloj.
          </p>
        </div>
      )}
      {/* S145: cualquier usuario del tenant puede renombrar la tarjeta del lienzo.
          La etiqueta no cambia qué hace el nodo (nodeType), solo cómo se lee el dibujo. */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-[#334155] mb-1">Nombre en el lienzo</label>
        <input
          value={node.data?.label || def.label || ""}
          onChange={e => onLabelChange && onLabelChange(e.target.value)}
          placeholder={def.label}
          maxLength={80}
          className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm w-full"
          data-testid="flow-node-label"
        />
        <p className="text-[11px] text-[#94A3B8] mt-1">
          Solo es el título de la tarjeta. El tipo sigue siendo «{def.label}».
        </p>
      </div>
      {def.config.length === 0 && <p className="text-xs text-[#94A3B8]">Este nodo no necesita más configuración.</p>}
      {def.config.filter(aplica).map(f => (
        <div key={f.key} className="mb-3">
          <label className="block text-xs font-medium text-[#334155] mb-1">
            {f.label}{f.required && <span className="text-red-500"> *</span>}
          </label>
          {f.type === 'multi' ? (
            <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#F1F5F9] max-h-56 overflow-y-auto"
                 data-testid={`flow-multi-${f.key}`}>
              {(refOptions[f.ref] || []).map(o => (
                <label key={o.value}
                       className="flex items-start gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-[#F8FAFC]">
                  <input type="checkbox" className="mt-0.5 shrink-0"
                         checked={valorMulti(f.key).includes(o.value)}
                         onChange={() => alternarMulti(f.key, o.value)} />
                  <span>
                    <span className="text-[#334155]">{o.label}</span>
                    {o.help && <span className="block text-[10px] text-[#94A3B8] leading-snug">{o.help}</span>}
                  </span>
                </label>
              ))}
              {/* Lista vacía: decir por qué, no dejar un recuadro mudo. */}
              {!(refOptions[f.ref] || []).length && (
                <p className="px-2 py-2 text-[11px] text-[#94A3B8]">
                  No hay funciones disponibles para el giro de tu negocio.
                </p>
              )}
            </div>
          ) : f.type === 'ref' ? (
            <div>
            <select value={cfg[f.key] || ''} onChange={e => setField(f.key, e.target.value)}
              className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm w-full">
              <option value="">— Elegir —</option>
              {(refOptions[f.ref] || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {!(refOptions[f.ref] || []).length && (
              <p className="text-[11px] text-amber-700 mt-1 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                {f.ref === 'email_template' && 'Aún no hay plantillas de email. Usa el botón «Crear plantilla de bienvenida» arriba o créala en Email.'}
                {f.ref === 'marketing_flow' && 'No hay automatizaciones de marketing. Créalas en Marketing → Automáticos, o quita este nodo si el flujo ya hace email/WhatsApp/IA.'}
                {f.ref === 'whatsapp_template' && 'No hay plantillas de WhatsApp aprobadas. Créalas y apruébalas en WhatsApp.'}
                {f.ref === 'campaign' && 'No hay campañas con formulario. Opcional si el lead entra por otro canal.'}
                {f.ref === 'assignment_pool' && 'No hay grupos de asignación. Se usará el grupo por defecto del negocio.'}
                {!['email_template','marketing_flow','whatsapp_template','campaign','assignment_pool'].includes(f.ref) && 'No hay opciones aún en el sistema para este campo.'}
              </p>
            )}
            </div>
          ) : f.type === 'select' ? (
            <select value={cfg[f.key] ?? f.default ?? ''} onChange={e => setField(f.key, e.target.value)}
              className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm w-full">
              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : f.type === 'boolean' ? (
            <label className="flex items-center gap-2 text-sm text-[#334155]">
              <input type="checkbox" checked={!!(cfg[f.key] ?? f.default)} onChange={e => setField(f.key, e.target.checked)} />
              Sí
            </label>
          ) : f.type === 'number' ? (
            <div>
              <input type="number" step="any" min={f.min ?? undefined}
                value={cfg[f.key] ?? f.default ?? ''} onChange={e => setField(f.key, e.target.value)}
                className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm w-full"
                data-testid={`flow-num-${f.key}`} />
              {f.help && <p className="text-[11px] text-[#64748B] mt-1 leading-snug">{f.help}</p>}
              {f.key === 'starts_in_hours' && (() => {
                const when = previewHoursFromNow(cfg[f.key] ?? f.default);
                const h = Number(cfg[f.key] ?? f.default);
                if (!when) return null;
                return (
                  <div className="mt-2 rounded-lg border border-sky-100 bg-sky-50 px-2.5 py-2 text-[11px] text-sky-900 leading-snug"
                       data-testid="flow-starts-in-preview">
                    <p className="font-semibold mb-0.5">Ejemplo si el flujo corre ahora</p>
                    <p>La cita se agendaría para: <strong>{when}</strong></p>
                    <p className="text-sky-800/80 mt-1">
                      Eso es «dentro de {Number.isFinite(h) ? h : '?'} hora(s)», no «a las {Number.isFinite(h) ? h : '?'} en punto».
                    </p>
                  </div>
                );
              })()}
            </div>
          ) : f.type === 'textarea' ? (
            <textarea rows={3} value={cfg[f.key] || ''} onChange={e => setField(f.key, e.target.value)}
              className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm w-full" />
          ) : (
            <input value={cfg[f.key] || ''} onChange={e => setField(f.key, e.target.value)}
              className="border border-[#E2E8F0] rounded-lg px-2 py-1.5 text-sm w-full" />
          )}
          {f.help && <p className="text-[11px] text-[#94A3B8] mt-1">{f.help}</p>}
          {(f.type === 'textarea' || f.type === 'text') && (refOptions.variable || []).length > 0 && (
            <details className="mt-1" data-testid={`flow-vars-${f.key}`}>
              <summary className="text-[11px] text-[#F72585] cursor-pointer select-none">
                Puedes usar variables
              </summary>
              <div className="mt-1 flex flex-wrap gap-1">
                {(refOptions.variable || []).map(v => (
                  <button key={v.value} type="button" title={v.help}
                    onClick={() => setField(f.key, `${cfg[f.key] || ''}${v.label}`)}
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-[#E2E8F0] bg-[#F8FAFC] hover:bg-[#FDF2F8]">
                    {v.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-1">
                Se reemplazan al enviar. Si el dato falta, se pone una alternativa
                ({'{nombre}'} → «ahí») para que la frase no quede coja.
              </p>
            </details>
          )}
        </div>
      ))}
      <button onClick={onDelete} className="mt-2 text-xs text-red-600 hover:underline flex items-center gap-1">
        <Trash2 className="w-3.5 h-3.5" /> Eliminar nodo
      </button>
    </div>
  );
}

// ── The canvas editor ─────────────────────────────────────────────────
// Se exporta SOLO para el guardia de render: la aplicación entra siempre por
// FlowBuilder, que elige el flujo antes. Sin esto no había forma de montar el
// lienzo en una prueba —hace falta un clic— y por eso ningún defecto de esta
// pantalla saltó nunca de forma automática.
export function FlowEditor({ flow, catalog, onBack, onSaved }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(flow.graph?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flow.graph?.edges || []);
  const [selectedId, setSelectedId] = useState(null);
  const [name, setName] = useState(flow.name);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [publishInfo, setPublishInfo] = useState(null);
  const [conflictModal, setConflictModal] = useState(null); // { conflicts, resolutions }
  const [activating, setActivating] = useState(false);
  const wrapper = useRef(null);
  const { screenToFlowPosition } = useReactFlow();

  const publishFlow = async () => {
    try {
      await api.put(`/tenant/flows/${flow.id}`, { name, graph: sanitizeGraph(nodes, edges) });
      const r = await api.post(`/tenant/flows/${flow.id}/publish`);
      setPublishInfo(r.data);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onConnect = useCallback(params => setEdges(eds => addEdge({
    ...params,
    animated: true,
    // Conserva sourceHandle/targetHandle (laterales o Sí/No) al guardar
  }, eds)), [setEdges]);

  // S145c — exportar el dibujo completo (nodos, posiciones, aristas, handles)
  const downloadJson = useCallback(() => {
    const payload = {
      version: 1,
      exported_at: new Date().toISOString(),
      name,
      status: flow.status || 'draft',
      graph: { nodes, edges },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(name || 'flujo').replace(/[^\w\-]+/g, '_').slice(0, 60)}.flow.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Flujo descargado en JSON');
  }, [name, nodes, edges, flow.status]);

  const fileInputRef = useRef(null);

  const loadFromJson = useCallback(async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const graph = data.graph || data;
      const incomingNodes = graph.nodes || data.nodes;
      const incomingEdges = graph.edges || data.edges;
      if (!Array.isArray(incomingNodes) || !incomingNodes.length) {
        toast.error('El JSON no trae nodos válidos');
        return;
      }
      // Normaliza: cada nodo necesita type cavernico + data.nodeType
      const catalogKeys = new Set((catalog?.nodes || []).map(n => n.key));
      const cleanedNodes = [];
      for (const n of incomingNodes) {
        const nodeType = n.data?.nodeType || n.nodeType || n.type;
        if (!nodeType || (catalogKeys.size && !catalogKeys.has(nodeType) && nodeType !== 'cavernico')) {
          // allow if data.nodeType is valid
          const nt = n.data?.nodeType;
          if (!nt || (catalogKeys.size && !catalogKeys.has(nt))) {
            toast.error(`Nodo desconocido o inválido: ${nodeType || '?'}`);
            return;
          }
        }
        const nt = n.data?.nodeType || nodeType;
        const def = (catalog?.nodes || []).find(d => d.key === nt);
        cleanedNodes.push({
          id: String(n.id || `n${Date.now()}_${cleanedNodes.length}`),
          type: 'cavernico',
          position: n.position || { x: 100 + cleanedNodes.length * 40, y: 100 + cleanedNodes.length * 30 },
          data: {
            nodeType: nt,
            label: n.data?.label || def?.label || nt,
            icon: n.data?.icon || def?.icon || 'Workflow',
            category: n.data?.category || def?.category || 'control',
            config: n.data?.config || n.config || {},
          },
        });
      }
      const idSet = new Set(cleanedNodes.map(n => n.id));
      const cleanedEdges = (incomingEdges || [])
        .filter(e => e && idSet.has(e.source) && idSet.has(e.target))
        .map((e, i) => ({
          id: e.id || `e-import-${i}`,
          source: e.source,
          target: e.target,
          sourceHandle: e.sourceHandle ?? undefined,
          targetHandle: e.targetHandle ?? undefined,
          animated: e.animated !== false,
        }));
      if (data.name && typeof data.name === 'string') setName(data.name.slice(0, 120));
      setNodes(cleanedNodes);
      setEdges(cleanedEdges);
      setSelectedId(null);
      toast.success(`Flujo cargado: ${cleanedNodes.length} nodos, ${cleanedEdges.length} conexiones. Guarda para persistir.`);
    } catch (err) {
      toast.error('No se pudo leer el JSON del flujo');
    }
  }, [catalog, setNodes, setEdges]);
  // Drag an edge endpoint onto another node to reconnect it.
  const onReconnect = useCallback((oldEdge, newConn) => setEdges(eds => reconnectEdge(oldEdge, newConn, eds)), [setEdges]);
  // Click a connection to remove it (plus Supr/Backspace on selection).
  const onEdgeClick = useCallback((_, edge) => setEdges(eds => eds.filter(e => e.id !== edge.id)), [setEdges]);

  const onDrop = useCallback(event => {
    event.preventDefault();
    const key = event.dataTransfer.getData('application/flow-node');
    const def = catalog.nodes.find(n => n.key === key);
    if (!def) return;
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const id = `n${Date.now()}_${nodeSeq++}`;
    setNodes(nds => nds.concat({
      id, type: 'cavernico', position,
      data: { nodeType: def.key, label: def.label, icon: def.icon, category: def.category, config: {} },
    }));
  }, [catalog, screenToFlowPosition, setNodes]);

  const onDragOver = useCallback(event => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }, []);

  const selectedNode = nodes.find(n => n.id === selectedId) || null;

  const updateSelectedConfig = useCallback(config => {
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, config } } : n));
  }, [selectedId, setNodes]);

  const updateSelectedLabel = useCallback(label => {
    const clean = (label || '').trim().slice(0, 80);
    setNodes(nds => nds.map(n => {
      if (n.id !== selectedId) return n;
      const fallback = catalog?.nodes?.find(d => d.key === n.data?.nodeType)?.label || n.data?.nodeType || 'Nodo';
      return { ...n, data: { ...n.data, label: clean || fallback } };
    }));
  }, [selectedId, setNodes, catalog]);

  const deleteSelected = useCallback(() => {
    setNodes(nds => nds.filter(n => n.id !== selectedId));
    setEdges(eds => eds.filter(e => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
  }, [selectedId, setNodes, setEdges]);


  const activateFlow = async (resolutions = null) => {
    setActivating(true);
    try {
      await api.put(`/tenant/flows/${flow.id}`, { name, graph: sanitizeGraph(nodes, edges) });
      try {
        const r = await api.post(`/tenant/flows/${flow.id}/activate`, {
          resolutions: resolutions || {},
        });
        toast.success(
          r.data?.resolutions_applied?.length
            ? `Flujo activado · ${r.data.resolutions_applied.join(' · ')}`
            : 'Flujo activado'
        );
        setConflictModal(null);
        onSaved?.({ id: flow.id, name, graph: sanitizeGraph(nodes, edges), status: 'active' });
      } catch (e) {
        const d = e.response?.data?.detail;
        const payload = (typeof d === 'object' && d) || e.response?.data || {};
        if (e.response?.status === 409 || payload.code === 'flow_conflicts') {
          const conflicts = payload.conflicts || payload.all_conflicts || [];
          const initial = {};
          conflicts.forEach(c => {
            const rec = (c.options || []).find(o => o.recommended) || (c.options || [])[0];
            if (rec) initial[c.code] = rec.key;
          });
          setConflictModal({ conflicts, resolutions: initial });
          return;
        }
        if (payload.errors) {
          toast.error((payload.detail || 'Flujo incompleto') + ': ' + payload.errors.slice(0, 4).join(' · '));
        } else {
          toast.error(formatApiError(d));
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActivating(false);
    }
  };

  const save = async (status) => {
    setSaving(true);
    try {
      const graph = sanitizeGraph(nodes, edges);
      // sincroniza estado local con lo limpio (mantiene posiciones exactas)
      setNodes(graph.nodes);
      setEdges(graph.edges);
      const body = { name, graph };
      if (status) body.status = status;
      await api.put(`/tenant/flows/${flow.id}`, body);
      toast.success(status === 'active' ? 'Flujo activado' : 'Flujo guardado (acomodo conservado)');
      // refresca lista sin remount del editor ni sobrescribir posiciones
      onSaved?.({ id: flow.id, name, graph, status: status || flow.status });
    } catch (e) {
        const d = e.response?.data?.detail;
        if (d && typeof d === 'object' && Array.isArray(d.errors)) {
          toast.error((d.detail || 'Flujo incompleto') + ': ' + d.errors.slice(0, 4).join(' · '));
        } else {
          toast.error(formatApiError(d));
        }
      } finally { setSaving(false); }
  };

  const runTest = async () => {
    if (!nodes.length) return toast.error('Agrega nodos antes de probar');
    setTesting(true); setTestResult(null);
    try {
      // save first so the run uses the latest graph
      await api.put(`/tenant/flows/${flow.id}`, { name, graph: sanitizeGraph(nodes, edges) });
      const r = await api.post(`/tenant/flows/${flow.id}/run`, { dry_run: true });
      // map node_id → result, in graph order
      const ctx = r.data.context || {};
      const steps = nodes.filter(n => ctx[n.id] !== undefined).map(n => ({ label: n.data.label, result: ctx[n.id] }));
      setTestResult({ status: r.data.status, steps });
      toast.success('Simulación completada (sin enviar nada real)');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setTesting(false); }
  };

  const byCat = useMemo(() => {
    const groups = {};
    (catalog.nodes || []).forEach(n => { (groups[n.category] ||= []).push(n); });
    return groups;
  }, [catalog]);

  return (
    <div className="flex flex-col" style={{ height: '78vh' }}>
      <div className="flex items-center gap-2 mb-2">
        <Button variant="outline" size="sm" onClick={onBack} className="flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Volver</Button>
        <input value={name} onChange={e => setName(e.target.value)}
          className="flex-1 border border-[#E2E8F0] rounded-lg px-3 py-1.5 text-sm font-medium" />
        <Button variant="outline" size="sm" onClick={runTest} disabled={testing} className="flex items-center gap-1">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Probar
        </Button>
        <Button variant="outline" size="sm" onClick={publishFlow} className="flex items-center gap-1" title="Solo genera el código HTML del formulario web. NO activa el flujo.">
          <Share2 className="w-4 h-4" /> Publicar
        </Button>
        <Button variant="outline" size="sm" onClick={downloadJson} className="flex items-center gap-1" title="Descargar flujo en JSON">
          <Download className="w-4 h-4" /> JSON
        </Button>
        <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1" title="Cargar flujo desde JSON">
          <Upload className="w-4 h-4" /> Cargar
        </Button>
        <input ref={fileInputRef} type="file" accept="application/json,.json,.flow.json" className="hidden"
          onChange={e => { loadFromJson(e.target.files?.[0]); e.target.value = ''; }} />
        <Button variant="outline" size="sm" onClick={() => save()} disabled={saving} className="flex items-center gap-1"
          title="Guarda nodos, conexiones y posiciones del acomodo">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </Button>
        <Button variant="outline" size="sm" onClick={async () => {
          try {
            const r = await api.post(`/tenant/flows/${flow.id}/ensure-email-template`);
            if (r.data.created) toast.success('Plantilla de bienvenida creada — elígela en el nodo Enviar email');
            else toast.message(`Ya tienes ${r.data.templates?.length || 0} plantilla(s) de email`);
            // re-fetch email template refs if panel open
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }} title="Crea una plantilla de email lista para el nodo Enviar email">
          Plantilla email
        </Button>
        <Button size="sm" onClick={() => activateFlow()} disabled={saving || activating}
          className="bg-[#F72585] hover:bg-[#d91d70] text-white"
          title="Activa el flujo para que corra con leads reales. Distinto de Formulario web.">
          {activating ? 'Activando…' : 'Activar flujo'}
        </Button>
      </div>

      
      <div className="mb-2 text-[11px] text-[#64748B] bg-slate-50 border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid="flow-activate-help">
        <strong className="text-[#0F172A]">Importante:</strong> <em>Formulario web</em> solo da el código HTML para tu página.
        Para que el flujo atienda leads de verdad debes pulsar <strong>Activar flujo</strong> (estado Activo, no Borrador).
        Cada nodo con asterisco rojo debe tener su plantilla / grupo / automatización elegida.
      </div>

      {conflictModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="flow-conflict-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5">
            <h3 className="text-base font-semibold text-[#0F172A] mb-1">Este flow choca con la configuración del sistema</h3>
            <p className="text-xs text-[#64748B] mb-4">
              No corrompemos nada en silencio: elige cómo resolver cada punto. Aplica a todos los negocios.
            </p>
            <div className="space-y-4">
              {(conflictModal.conflicts || []).map(c => (
                <div key={c.code} className="border border-[#E2E8F0] rounded-lg p-3">
                  <p className="text-sm font-medium text-[#0F172A]">{c.title}</p>
                  <p className="text-xs text-[#64748B] mt-1 mb-2">{c.message}</p>
                  <div className="space-y-1.5">
                    {(c.options || []).map(o => (
                      <label key={o.key} className="flex items-start gap-2 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name={`conflict-${c.code}`}
                          checked={conflictModal.resolutions[c.code] === o.key}
                          onChange={() => setConflictModal(m => ({
                            ...m,
                            resolutions: { ...m.resolutions, [c.code]: o.key },
                          }))}
                          className="mt-0.5"
                        />
                        <span className={o.recommended ? 'text-[#0F172A] font-medium' : 'text-[#475569]'}>
                          {o.label}{o.recommended ? ' · recomendado' : ''}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" size="sm" onClick={() => setConflictModal(null)}>Cancelar</Button>
              <Button size="sm" className="bg-[#F72585] hover:bg-[#d91d70] text-white"
                disabled={activating}
                onClick={() => activateFlow(conflictModal.resolutions)}>
                {activating ? 'Aplicando…' : 'Aplicar y activar'}
              </Button>
            </div>
          </div>
        </div>
      )}


      {publishInfo && (
        <div className="mb-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3 text-xs" data-testid="flow-publish-info">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-emerald-800 flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Formulario web publicado</span>
            <button onClick={() => setPublishInfo(null)} className="text-emerald-600 hover:text-emerald-900"><X className="w-3.5 h-3.5" /></button>
          </div>
          <p className="text-emerald-700 mb-1">Pega este código en tu sitio. Cada envío crea un lead y arranca este flujo solo (el flujo debe estar <b>Activo</b>).</p>
          <textarea readOnly value={publishInfo.embed_html} onClick={e => e.target.select()}
            className="w-full h-28 border border-emerald-200 rounded p-2 font-mono text-[11px] bg-white text-slate-700" />
          <button onClick={() => { navigator.clipboard?.writeText(publishInfo.embed_html); toast.success('Código copiado'); }}
            className="mt-1 text-emerald-700 hover:underline">Copiar código</button>
        </div>
      )}

      {testResult && (
        <div className="mb-2 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-xs" data-testid="flow-test-result">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold">Simulación (sin enviar nada real) · {testResult.status}</span>
            <button onClick={() => setTestResult(null)} className="text-slate-400 hover:text-white"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {testResult.steps.map((s, i) => (
              <span key={i} className="bg-slate-700 rounded px-2 py-0.5">{s.label}: <span className="text-emerald-300">{s.result}</span></span>
            ))}
            {testResult.steps.length === 0 && <span className="text-slate-400">Sin pasos ejecutados — conecta los nodos desde una entrada.</span>}
          </div>
        </div>
      )}

      <div className="flex flex-1 border border-[#E2E8F0] rounded-xl overflow-hidden">
        {/* Palette */}
        <div className="w-56 border-r border-[#E2E8F0] bg-[#F8FAFC] p-3 overflow-y-auto" data-testid="flow-palette">
          <p className="text-[11px] text-[#94A3B8] mb-1">Arrastra un nodo al lienzo →</p>
          <p className="text-[11px] text-[#CBD5E1] mb-2">
            Conecta por los puntos de los 4 lados. Arrastra nodos para acomodar; al Guardar se conserva el layout.
            Clic en una conexión para borrarla.
          </p>
          {(catalog.categories || []).map(cat => (
            <div key={cat.key} className="mb-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: (CAT_COLORS[cat.key] || {}).text }}>{cat.label}</p>
              {(byCat[cat.key] || []).map(n => {
                const Icon = ICONS[n.icon] || Workflow;
                const c = CAT_COLORS[n.category] || CAT_COLORS.control;
                return (
                  <div key={n.key} draggable
                    onDragStart={e => e.dataTransfer.setData('application/flow-node', n.key)}
                    title={n.help}
                    className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded-lg border border-[#E2E8F0] bg-white cursor-grab hover:shadow-sm">
                    <span className="rounded p-1" style={{ background: c.border }}><Icon className="w-3 h-3 text-white" /></span>
                    <span className="text-xs text-[#334155]">{n.label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Canvas */}
        <div className="flex-1" ref={wrapper} onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            onReconnect={onReconnect} onEdgeClick={onEdgeClick} edgesReconnectable
            connectionMode="loose"
            deleteKeyCode={['Backspace', 'Delete']}
            onNodeClick={(_, n) => setSelectedId(n.id)} onPaneClick={() => setSelectedId(null)}
            fitView={nodes.length > 0 && nodes.every(n => !n.position || (n.position.x === 0 && n.position.y === 0))}
            proOptions={{ hideAttribution: true }}>
            <Background />
            <Controls />
            <MiniMap zoomable pannable />
          </ReactFlow>
        </div>

        {/* Config */}
        {selectedNode && (
          <NodeConfigPanel node={selectedNode} catalog={catalog}
            onChange={updateSelectedConfig} onLabelChange={updateSelectedLabel} onClose={() => setSelectedId(null)} onDelete={deleteSelected} />
        )}
      </div>
      {nodes.length === 0 && (
        <p className="text-xs text-[#94A3B8] mt-2 text-center">
          Empieza arrastrando un nodo de <strong>Entrada</strong> (WhatsApp de sucursal, Formulario o Llamada IA), luego conéctalo a los siguientes pasos.
        </p>
      )}
    </div>
  );
}

// ── Top-level: list of flows ↔ editor ─────────────────────────────────
export default function FlowBuilder() {
  const [catalog, setCatalog] = useState(null);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFlow, setOpenFlow] = useState(null);
  const [aiDesc, setAiDesc] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  const loadList = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/tenant/flows/node-catalog'),
      api.get('/tenant/flows'),
    ]).then(([c, f]) => { setCatalog(c.data); setFlows(f.data); })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const createFlow = async () => {
    try {
      const r = await api.post('/tenant/flows', { name: 'Nuevo flujo' });
      const full = await api.get(`/tenant/flows/${r.data.id}`);
      setOpenFlow(full.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openExisting = async (id) => {
    try { const r = await api.get(`/tenant/flows/${id}`); setOpenFlow(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generateFromAttendance = async () => {
    try {
      const r = await api.post('/tenant/flows/from-attendance');
      toast.success('Flujo generado desde tu configuración de Atención inmediata');
      const full = await api.get(`/tenant/flows/${r.data.id}`);
      setOpenFlow(full.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [sysGenerating, setSysGenerating] = useState(false);
  const generateFromSystemConfig = async () => {
    setSysGenerating(true);
    try {
      const r = await api.post('/tenant/flows/from-system-config');
      const n = r.data?.summary?.nodes || '?';
      const warns = (r.data?.conflicts_if_activated || []).length;
      toast.success(
        warns
          ? `Flow armado (${n} nodos). Al activar te pediremos resolver ${warns} posible(s) choque(s) con la config.`
          : `Flow armado desde tu configuración (${n} nodos). Revísalo y actívalo.`
      );
      const full = await api.get(`/tenant/flows/${r.data.id}`);
      setOpenFlow(full.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSysGenerating(false); }
  };


  const generateWithAI = async () => {
    if (!aiDesc.trim()) return toast.error('Describe el flujo que quieres crear');
    setAiGenerating(true);
    try {
      const gen = await api.post('/tenant/flows/ai-generate', { description: aiDesc });
      const created = await api.post('/tenant/flows', { name: gen.data.name });
      await api.put(`/tenant/flows/${created.data.id}`, { graph: gen.data.graph });
      const full = await api.get(`/tenant/flows/${created.data.id}`);
      toast.success('Flujo creado con IA — revísalo y actívalo');
      setAiDesc('');
      setOpenFlow(full.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setAiGenerating(false); }
  };

  const deleteFlow = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este flujo?')) return;
    try { await api.delete(`/tenant/flows/${id}`); toast.success('Flujo eliminado'); loadList(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  if (openFlow && catalog) {
    return (
      <ReactFlowProvider>
        <FlowEditor key={openFlow.id} flow={openFlow} catalog={catalog}
          onBack={() => { setOpenFlow(null); loadList(); }}
          onSaved={(patch) => {
            // actualiza metadata del flujo abierto sin remount destructivo
            if (patch?.graph) {
              setOpenFlow(prev => prev ? { ...prev, name: patch.name || prev.name, graph: patch.graph, status: patch.status || prev.status } : prev);
            }
            loadList();
          }} />
      </ReactFlowProvider>
    );
  }

  return (
    <div className="space-y-4" data-testid="flow-builder">
      <AutomationsMap current="flows" />
      <SectionHelp module="flows" title="Ruta del lead (Constructor de flujos)"
        description="Dibuja el camino cuando entra un lead: canal de entrada → IA → asignación → llamadas → etapas. Arrastra, conecta y activa. Las secuencias largas de email/WhatsApp viven en Marketing → Secuencias; las reglas de un solo aviso en Config IA → Reglas al instante."
        whatItDoesNot="Esto NO es un envío masivo ni una secuencia de nurturing. Es la ruta de atención del lead en el momento en que llega." />

      
      <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4" data-testid="flow-from-system">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-emerald-900">Armar flow desde mi configuración</h3>
            <p className="text-xs text-emerald-800 mt-1">
              Lee lo que ya tienes (Atención inmediata, ruleta, plantillas, marketing, voz) y propone un flow listo.
              No cambia tu config sola: al activar, si hay choques, te pedimos elegir.
            </p>
          </div>
          <Button onClick={generateFromSystemConfig} disabled={sysGenerating}
            className="shrink-0 bg-emerald-600 hover:bg-emerald-700 flex items-center gap-1">
            {sysGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Workflow className="w-4 h-4" />}
            Armar desde config
          </Button>
        </div>
      </div>

<div className="border border-violet-200 bg-violet-50 rounded-xl p-4" data-testid="flow-ai-assistant">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-violet-800">Crea tu flujo con IA</h3>
        </div>
        <p className="text-xs text-violet-700 mb-2">Descríbelo en tus palabras y la IA lo arma. Ej: "Cuando entre un lead por WhatsApp, que conteste la IA, luego se asigne por ruleta, y a los 2 días si no respondió mándale un email."</p>
        <div className="flex gap-2">
          <input value={aiDesc} onChange={e => setAiDesc(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') generateWithAI(); }}
            placeholder="Describe tu flujo…"
            className="flex-1 border border-violet-200 rounded-lg px-3 py-2 text-sm" data-testid="flow-ai-input" />
          <Button onClick={generateWithAI} disabled={aiGenerating} className="flex items-center gap-1 bg-violet-600 hover:bg-violet-700">
            {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2"><Workflow className="w-4 h-4" /> Tus flujos</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={generateFromAttendance} className="flex items-center gap-1">
            <Sparkles className="w-4 h-4" /> Desde Atención inmediata
          </Button>
          <Button onClick={createFlow} className="flex items-center gap-1"><Plus className="w-4 h-4" /> Nuevo flujo</Button>
        </div>
      </div>

      {flows.length === 0 ? (
        <div className="border border-dashed border-[#E2E8F0] rounded-xl p-10 text-center">
          <Workflow className="w-10 h-10 text-[#CBD5E1] mx-auto mb-2" />
          <p className="text-sm text-[#64748B]">Todavía no tienes flujos. Crea el primero y arrástra tus nodos.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {flows.map(f => (
            <div key={f.id} onClick={() => openExisting(f.id)}
              className="border border-[#E2E8F0] rounded-xl p-4 bg-white hover:shadow-md cursor-pointer" data-testid="flow-card">
              <div className="flex items-start justify-between">
                <h3 className="text-sm font-semibold text-[#0F172A]">{f.name}</h3>
                <button onClick={e => deleteFlow(f.id, e)} className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              {f.source === 'attendance' && (
                <span className="inline-flex items-center gap-1 text-[10px] mt-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                  <Sparkles className="w-3 h-3" /> Generado desde Atención inmediata
                </span>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${f.status === 'active' ? 'bg-emerald-100 text-emerald-700' : f.status === 'paused' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                  {f.status === 'active' ? 'Activo' : f.status === 'paused' ? 'Pausado' : 'Borrador'}
                </span>
                <span className="text-[11px] text-[#94A3B8]">{f.node_count} nodo{f.node_count === 1 ? '' : 's'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
